import { GoogleLogin } from '@react-oauth/google';
import { AxiosError } from 'axios';
import getBlockchainErrorMessage from 'common/blockchain/getBlockchainError.ts';
import { addressToBeSigned, MPOST } from 'common/helpers.ts';
import React, { useCallback } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import useSWRMutation from 'swr/mutation';
import { Button } from 'ui/component/Button.tsx';
import { Divider } from 'ui/component/Divider.tsx';
import { Input } from 'ui/control/Input.tsx';
import { getTranslatedError } from '#admin/helpers.ts';
import { useBlockchain } from '#admin/hook/useBlockchain.tsx';
import { actions } from '#admin/reducer/app.ts';
import type { AppDispatch } from '#admin/store.ts';

type FormValues = {
  email: string;
  password: string;
};

type Props = {
  onSuccess: string;
};

export const Auth: React.FC<Props> = ({ onSuccess }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const { auth, wallet } = useBlockchain();
  const [ssoLoading, setSsoLoading] = React.useState(false);
  const [blockchainError, setBlockchainError] = React.useState<any | null>(null);

  const formMethods = useForm<FormValues>({
    mode: 'onTouched',
    criteriaMode: 'all',
    shouldUnregister: true,
    shouldUseNativeValidation: false,
    shouldFocusError: true,
    defaultValues: import.meta.env.VITE_APP_ENV === 'local' ? { email: 'admin@atlas-path.com', password: 'Password123!' } : {},
  });
  const { handleSubmit, formState } = formMethods;

  const { trigger: triggerLogin, error: loginError } = useSWRMutation<FormValues>('/auth/login', MPOST);
  const { trigger: triggerOauth, error: oauthError } = useSWRMutation('/auth/oauth-callback', MPOST);

  const error = loginError || oauthError || blockchainError;

  const getErrorMessage = useCallback((t: any, error: any, defaultMessage: string) => {
    if (error instanceof AxiosError) {
      return getTranslatedError(error, t);
    }

    return getBlockchainErrorMessage(t, error, defaultMessage);
  }, []);

  const onSubmit = async (values: FormValues) => {
    if (!auth) return;

    let encodedSignature: string;

    try {
      const signature = await auth.login(values.email, values.password);
      // we need to encode the signature to base64 to overcome WAF blocking hexadecimal strings in urls and payloads
      encodedSignature = btoa(signature);
    } catch (e) {
      setBlockchainError(e);
      throw e;
    }

    // @ts-expect-error
    const token: string = await triggerLogin({ email: values.email, signature: encodedSignature });
    dispatch(actions.login({ token }));
    navigate(onSuccess);
  };

  React.useEffect(() => {
    const token = searchParams.get('token');
    const next = searchParams.get('next');
    if (token) {
      dispatch(actions.login({ token }));
      const safeNext = next && next.startsWith('/') ? next : onSuccess;
      navigate(safeNext);
    }
  }, [searchParams, dispatch, navigate, onSuccess]);

  const onOauthSignIn = React.useCallback(
    async (credentialResponse: any) => {
      if (!wallet) return;

      const ssoUserWallet = wallet.create();
      const ssoUserWalletAddress = await ssoUserWallet.getAddress();
      const signature = await ssoUserWallet.signMessage(addressToBeSigned(ssoUserWalletAddress));
      const walletData = btoa(JSON.stringify({ address: ssoUserWalletAddress, signature }));

      try {
        const token = await triggerOauth({ credentialResponse, walletData });
        const params = new URLSearchParams({ token }).toString();
        navigate(`/auth?${params}`);
      } catch (e) {
        setSsoLoading(false);
        console.error(e);
      }
    },
    [triggerOauth, navigate, wallet],
  );

  return (
    <main className="auth-gradient-background fixed top-0 left-0 h-screen w-screen overflow-auto">
      <FormProvider {...formMethods}>
        <form
          className="paper !absolute top-1/2 left-1/2 m-auto w-[480px] -translate-x-1/2 -translate-y-1/2 border pt-16"
          autoCorrect={'off'}
          autoComplete={'off'}
          onSubmit={handleSubmit(onSubmit)}
          noValidate={true}
        >
          <div className="flex flex-col items-center gap-4 p-8">
            <div className="mb-5 flex w-full items-center justify-center">
              <img src={'/atlas-path-logo.svg'} width={380} alt="Atlas Path Logo" />
            </div>
            <GoogleLogin
              click_listener={() => setSsoLoading(true)}
              onError={() => setSsoLoading(false)}
              onSuccess={onOauthSignIn}
              size="large"
              width="999"
              text="continue_with"
              useOneTap={false}
              theme="outline"
            />

            <Divider className="mt-3">{t('auth.login.divider')}</Divider>

            <div className="mt-3 flex w-full flex-col items-center gap-4">
              <Input
                disabled={ssoLoading}
                className="w-full"
                name={'email'}
                label={t('auth.login.emailLabel')}
                type={'email'}
                registerOptions={{
                  validate: { required: (value) => (value ? undefined : t('auth.login.emailRequired')) },
                }}
              />
              <Input
                disabled={ssoLoading}
                className="w-full"
                name={'password'}
                label={t('auth.login.passwordLabel')}
                type={'password'}
                registerOptions={{ validate: { required: (value) => (value ? undefined : t('auth.login.passwordRequired')) } }}
              />
              <Button className="blue mt-4 gap-1" type="submit" disabled={!formState.isValid && !ssoLoading} loading={formState.isSubmitting || ssoLoading}>
                {t('auth.login.loginButton')}
              </Button>
            </div>
            {(error || (formState.isSubmitted && !formState.isSubmitSuccessful)) && (
              <section className="mt-4 grid grid-flow-col justify-center text-center text-red-700">{getErrorMessage(t, error, t('auth.login.invalidCredentials'))}</section>
            )}
            <Link to="/password-request" className="button-link flex h-10 w-full items-center justify-center">
              {t('auth.login.forgotPassword')}
            </Link>
          </div>
        </form>
      </FormProvider>
    </main>
  );
};
